import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Grid,
  IconButton,
  Paper,
  Box,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Alert,
  CircularProgress,
  Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import GuideButton from '../../common/guide/GuideButton';
import { ACCOUNTING_API, handleApiError } from '../../../config/api';
import { accountingApi } from '../../../config/apiClient';
import { useTranslation } from 'react-i18next';

interface ShippingAddress {
  id?: string;
  country: string;
  city: string;
  district: string;
  authorizedPerson: string;
  postalCode: string;
  phone: string;
  mobilePhone: string;
  email: string;
  address: string;
  createdAt?: string;
  isNew?: boolean; // Sadece UI için kullanılacak, yeni eklenen adresleri işaretlemek için
}

interface ShippingAddressModalProps {
  open: boolean;
  onClose: () => void;
  accountId: string | null;
  companyId: string | null;
  accountName: string;
}

const ShippingAddressModal: React.FC<ShippingAddressModalProps> = ({
  open,
  onClose,
  accountId,
  companyId,
  accountName
}) => {
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);
  const defaultCountry = isTr ? 'Türkiye' : 'Turkey';

  // Form durumu
  const [formData, setFormData] = useState<ShippingAddress>({
    country: defaultCountry,
    city: '',
    district: '',
    authorizedPerson: '',
    postalCode: '',
    phone: '',
    mobilePhone: '',
    email: '',
    address: ''
  });

  // Eklenen sevk adresleri listesi
  const [shippingAddresses, setShippingAddresses] = useState<ShippingAddress[]>([]);
  
  // Kayıtlı sevk adresleri listesi
  const [existingAddresses, setExistingAddresses] = useState<ShippingAddress[]>([]);
  
  // Hata ve yükleme durumları
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null);

  // Sayfa açıldığında mevcut sevk adreslerini yükle
  useEffect(() => {
    if (open && companyId && accountId) {
      loadExistingAddresses();
    }
    
  }, [open, companyId, accountId]);

  // Mevcut sevk adreslerini veritabanından yükleme (SQL API)
  const loadExistingAddresses = async () => {
    if (!companyId || !accountId) return;
    
    setLoadingAddresses(true);
    
    try {
      // accountId kontrolü - Sayısal ise SQL, değilse Firebase
      const numericAccountId = parseInt(accountId, 10);
      const isNumericAccountId = !isNaN(numericAccountId) && numericAccountId.toString() === accountId;

      if (isNumericAccountId) {
        // ✅ SQL ID - SQL API'den yükle
        console.log('📦 [SHIPPING ADDRESS] SQL API\'den sevk adresleri yükleniyor...');
        
        // ✅ MULTI-TENANT: companyId parametresi ekle
        if (!companyId) {
          console.error('❌ CompanyId bulunamadı');
          return;
        }
        
        const response = await accountingApi.get(ACCOUNTING_API.SHIPPING_ADDRESSES(numericAccountId));

        const result = response.data;
        const sqlAddresses = result.data || result || [];
        
        console.log('✅ [SHIPPING ADDRESS] SQL\'den yüklenen adres sayısı:', sqlAddresses.length);
        
        const addressesList: ShippingAddress[] = sqlAddresses.map((data: any) => ({
          id: data.id.toString(),
          country: data.country || defaultCountry,
          city: data.city || '',
          district: data.district || '',
          authorizedPerson: data.authorizedPerson || '',
          postalCode: data.postalCode || '',
          phone: data.phone || '',
          mobilePhone: data.mobilePhone || '',
          email: data.email || '',
          address: data.address || '',
          createdAt: data.createdAt || new Date().toISOString(),
          isNew: false
        }));
        
        // Kayıtlı adresleri createdAt'e göre sırala (en yeni en üstte)
        addressesList.sort((a, b) => {
          const dateA = new Date(a.createdAt || '').getTime();
          const dateB = new Date(b.createdAt || '').getTime();
          return dateB - dateA;
        });
        
        setExistingAddresses(addressesList);
      } else {
        // Legacy firebase-id hesap: tum cariler SQL'e tasindigi icin RTDB okumasi kaldirildi.
        console.warn('⚠️ [SHIPPING ADDRESS] Sayisal olmayan (legacy firebase) hesap id\'si; SQL karsiligi yok:', accountId);
        setExistingAddresses([]);
        setLoadingAddresses(false);
      }
    } catch (error) {
      console.error('❌ [SHIPPING ADDRESS] Sevk adresleri yüklenirken hata:', error);
      setError(handleApiError(error));
    } finally {
      if (isNaN(parseInt(accountId, 10))) {
        // Firebase için loading'i Firebase callback'te kapatıyoruz
        return;
      }
      setLoadingAddresses(false);
    }
  };

  // Form alanlarını temizle
  const resetForm = () => {
    setFormData({
      country: defaultCountry,
      city: '',
      district: '',
      authorizedPerson: '',
      postalCode: '',
      phone: '',
      mobilePhone: '',
      email: '',
      address: ''
    });
  };

  const handleCloseModal = () => {
    onClose();
  };

  // Form alanlarını güncelle
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Sevk adresi ekle
  const handleAddAddress = () => {
    // Validasyon: En azından şehir ve adres alanları doldurulmuş olmalı
    if (!formData.city.trim() || !formData.address.trim()) {
      setError(tt('En azından şehir ve adres alanları zorunludur.', 'At least city and address fields are required.'));
      return;
    }

    // Yeni adresi geçici ID ile ekle
    const newAddress: ShippingAddress = {
      ...formData,
      id: Date.now().toString(), // Geçici ID
      isNew: true
    };

    setShippingAddresses(prev => [...prev, newAddress]);
    resetForm();
    setError(null);
  };

  // Sevk adresi sil (yeni eklenenlerden)
  const handleRemoveAddress = (id: string | undefined) => {
    if (!id) return;
    setShippingAddresses(prev => prev.filter(address => address.id !== id));
  };

  // Kayıtlı sevk adresi sil
  const handleRemoveExistingAddress = async (id: string | undefined) => {
    if (!id || !companyId || !accountId) return;
    
    setDeleteLoading(id);
    
    try {
      // accountId kontrolü - Sayısal ise SQL, değilse Firebase
      const numericAccountId = parseInt(accountId, 10);
      const isNumericAccountId = !isNaN(numericAccountId) && numericAccountId.toString() === accountId;

      // addressId kontrolü
      const numericAddressId = parseInt(id, 10);
      const isNumericAddressId = !isNaN(numericAddressId) && numericAddressId.toString() === id;

      if (isNumericAccountId && isNumericAddressId) {
        // SQL ID - SQL API'ye istek gönder
        
        // ✅ MULTI-TENANT: companyId parametresi ekle
        if (!companyId) {
          throw new Error('CompanyId bulunamadı');
        }
        
        await accountingApi.post(ACCOUNTING_API.SHIPPING_ADDRESS_DELETE(numericAccountId, numericAddressId));

        console.log('Sevk adresi silindi (SQL)');
        
        // UI'ı güncelle
        setExistingAddresses(prev => prev.filter(address => address.id !== id));
      } else {
        // Legacy firebase-id hesap/adres: tum cariler SQL'e tasindigi icin RTDB silme kaldirildi.
        console.warn('⚠️ [SHIPPING ADDRESS] Sayisal olmayan (legacy firebase) id; SQL karsiligi yok, silme atlandi:', accountId, id);
      }
      
      setDeleteLoading(null);
    } catch (error) {
      console.error('Sevk adresi silinirken hata:', error);
      setError(handleApiError(error));
      setDeleteLoading(null);
    }
  };

  // Tüm adresleri SQL API'ye kaydet
  const handleSaveAll = async () => {
    if (!companyId || !accountId) {
      setError(tt('Şirket veya hesap bilgisi eksik.', 'Company or account information is missing.'));
      return;
    }

    if (shippingAddresses.length === 0) {
      setError(tt('Kaydedilecek sevk adresi bulunamadı.', 'No shipping address to save.'));
      return;
    }

    try {
      setSaveLoading(true);
      
      // accountId Firebase ID olabilir, SQL ID'ye çevirmemiz gerekiyor
      let accountIdToUse: string | number = accountId;
      
      // Eğer accountId sayısal bir string ise, number'a çevir
      const numericId = parseInt(accountId, 10);
      if (!isNaN(numericId) && numericId.toString() === accountId) {
        accountIdToUse = numericId;
      } else {
        // Legacy firebase-id hesap: tum cariler SQL'e tasindigi icin RTDB yazimi kaldirildi.
        console.warn('⚠️ [SHIPPING ADDRESS] Sayisal olmayan (legacy firebase) hesap id; SQL karsiligi yok, kayit atlandi:', accountId);
        setError(tt('Bu hesap icin sevk adresi kaydedilemedi (gecersiz hesap).', 'Shipping address could not be saved for this account (invalid account).'));
        setSaveLoading(false);
        return;
      }
      
      // ✅ SQL API - Her bir adresi SQL API'ye kaydet
      console.log('📦 [SHIPPING ADDRESS] SQL API\'ye sevk adresleri kaydediliyor...');
      
      let savedCount = 0;
      for (const address of shippingAddresses) {
        const { id, isNew, createdAt, ...addressData } = address; // Geçici ID, isNew ve createdAt'i çıkar
        
        const addressPayload = {
          companyId: companyId, // ✅ CompanyId ekle
          accountId: accountIdToUse, // ✅ AccountId ekle
          ...addressData,
        };

        console.log('📤 [SHIPPING ADDRESS] Gönderilen payload:', JSON.stringify(addressPayload, null, 2));

        // ✅ MULTI-TENANT: companyId parametresi ekle
        if (!companyId) {
          throw new Error('CompanyId bulunamadı');
        }

        await accountingApi.post(ACCOUNTING_API.SHIPPING_ADDRESSES(accountIdToUse as number), addressPayload);
        
        savedCount++;
        console.log(`✅ [SHIPPING ADDRESS] ${savedCount}/${shippingAddresses.length} adres kaydedildi`);
      }
      
      console.log('✅ [SHIPPING ADDRESS] Tüm sevk adresleri SQL\'e kaydedildi');
      
      // Listeyi yeniden yükle
      await loadExistingAddresses();
      
      setSaveLoading(false);
      setShippingAddresses([]); // Adresleri temizle
      handleCloseModal(); // Modalı kapat
    } catch (error: any) {
      console.error('Sevk adresleri kaydedilirken hata:', error);
      setError(handleApiError(error));
      setSaveLoading(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        sx={{ '& .MuiDialog-paper': { borderRadius: 2 } }}
      >
        <DialogTitle
          sx={{
            bgcolor: 'primary.main',
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <LocalShippingIcon sx={{ mr: 1 }} />
            <Typography variant="h6">{tt('Sevk Adresleri', 'Shipping Addresses')}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GuideButton guideKey="accounts.shippingAddresses" color="inherit" />
            <IconButton edge="end" color="inherit" onClick={handleCloseModal} aria-label="kapat">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>

        {/* Cari bilgisi başlığı */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" fontWeight="bold">
            {accountName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {tt('Cari hesap için sevk adresleri ekleyebilirsiniz.', 'You can add shipping addresses for this current account.')}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Adres ekleme formu */}
        <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
            {tt('Yeni Sevk Adresi', 'New Shipping Address')}
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label={tt('Ülke', 'Country')}
                name="country"
                value={formData.country}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label={tt('İl', 'City')}
                name="city"
                value={formData.city}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label={tt('İlçe', 'District')}
                name="district"
                value={formData.district}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label={tt('Yetkili Kişi', 'Authorized Person')}
                name="authorizedPerson"
                value={formData.authorizedPerson}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label={tt('Posta Kodu', 'Postal Code')}
                name="postalCode"
                value={formData.postalCode}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label={tt('Telefon', 'Phone')}
                name="phone"
                value={formData.phone}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label={tt('Cep Telefonu', 'Mobile Phone')}
                name="mobilePhone"
                value={formData.mobilePhone}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label={tt('E-posta', 'Email')}
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={4} sx={{ display: 'flex', alignItems: 'center' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddAddress}
                startIcon={<AddIcon />}
                sx={{ height: '40px' }}
                fullWidth
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : tt('Adres Ekle', 'Add Address')}
              </Button>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label={tt('Sevk Adresi', 'Shipping Address')}
                name="address"
                value={formData.address}
                onChange={handleChange}
                multiline
                rows={2}
                required
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Eklenen adresler listesi */}
        {shippingAddresses.length > 0 && (
          <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
              {tt('Eklenen Sevk Adresleri', 'Added Shipping Addresses')}
              <Chip 
                label={`${tt('Yeni', 'New')} (${shippingAddresses.length})`} 
                color="primary" 
                size="small" 
                sx={{ ml: 1 }} 
              />
            </Typography>
            
            <List>
              {shippingAddresses.map((address, index) => (
                <React.Fragment key={address.id}>
                  {index > 0 && <Divider />}
                  <ListItem alignItems="flex-start" sx={{ py: 1 }}>
                    <ListItemText
                      primary={
                        <Typography variant="body1" fontWeight="medium">
                          {address.city}, {address.district} - {address.authorizedPerson || tt('Belirtilmemiş', 'Not specified')}
                        </Typography>
                      }
                      secondary={
                        <>
                          <Typography variant="body2" color="text.primary" component="span">
                            {address.address}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" component="div">
                            {address.phone && `${tt('Tel', 'Phone')}: ${address.phone}`}
                            {address.mobilePhone && ` | ${tt('Cep', 'Mobile')}: ${address.mobilePhone}`}
                            {address.email && ` | ${tt('E-posta', 'Email')}: ${address.email}`}
                          </Typography>
                        </>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton 
                        edge="end" 
                        aria-label="delete" 
                        onClick={() => handleRemoveAddress(address.id)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          </Paper>
        )}

        {/* Mevcut kayıtlı adresler listesi */}
        {loadingAddresses ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : existingAddresses.length > 0 && (
          <Paper elevation={2} sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
              {tt('Kayıtlı Sevk Adresleri', 'Saved Shipping Addresses')}
              <Chip 
                label={`${existingAddresses.length}`} 
                color="secondary" 
                size="small" 
                sx={{ ml: 1 }} 
              />
            </Typography>
            
            <List>
              {existingAddresses.map((address, index) => (
                <React.Fragment key={address.id}>
                  {index > 0 && <Divider />}
                  <ListItem alignItems="flex-start" sx={{ py: 1 }}>
                    <ListItemText
                      primary={
                        <Typography variant="body1" fontWeight="medium">
                          {address.city}, {address.district} - {address.authorizedPerson || tt('Belirtilmemiş', 'Not specified')}
                        </Typography>
                      }
                      secondary={
                        <>
                          <Typography variant="body2" color="text.primary" component="span">
                            {address.address}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" component="div">
                            {address.phone && `${tt('Tel', 'Phone')}: ${address.phone}`}
                            {address.mobilePhone && ` | ${tt('Cep', 'Mobile')}: ${address.mobilePhone}`}
                            {address.email && ` | ${tt('E-posta', 'Email')}: ${address.email}`}
                          </Typography>
                        </>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton 
                        edge="end" 
                        aria-label="delete" 
                        onClick={() => handleRemoveExistingAddress(address.id)}
                        color="error"
                        disabled={deleteLoading === address.id}
                      >
                        {deleteLoading === address.id ? (
                          <CircularProgress size={20} />
                        ) : (
                          <DeleteIcon />
                        )}
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          </Paper>
        )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseModal} variant="outlined">
            {tt('İptal', 'Cancel')}
          </Button>
          <Button 
            onClick={handleSaveAll} 
            variant="contained" 
            color="primary"
            disabled={saveLoading || shippingAddresses.length === 0}
          >
            {saveLoading ? (
              <>
                <CircularProgress size={24} sx={{ mr: 1 }} color="inherit" />
                {tt('Kaydediliyor...', 'Saving...')}
              </>
            ) : (
              tt('Kaydet', 'Save')
            )}
          </Button>
        </DialogActions>
      </Dialog>

    </>
  );
};

export default ShippingAddressModal; 